import { categorizeTodosBySection, SectionType, TodoSection } from '../../../modules/TodoSection'
import { FilterType, SortType } from '../../../modules/TodoFilter'
import { TodoItem } from '../../../modules/TodoItem'

/**
 * Active와 Completed 섹션을 생성하는 헬퍼 함수
 * @param sortedAndFilteredTodos - 정렬 및 필터링된 todo 리스트
 * @param completedSectionKey - Completed 섹션 키
 * @param t - i18n translation 함수
 * @returns 섹션 배열
 */
const createActiveAndCompletedSections = (
  sortedAndFilteredTodos: TodoItem[],
  completedSectionKey: string,
  t: (key: string) => string,
): TodoSection[] => {
  const activeTodos = sortedAndFilteredTodos.filter((todo) => todo.status !== 'COMPLETED')
  const completedTodos = sortedAndFilteredTodos.filter((todo) => todo.status === 'COMPLETED')

  const sections: TodoSection[] = []

  if (activeTodos.length > 0) {
    sections.push({
      key: SectionType.Active,
      title: '',
      count: activeTodos.length,
      todos: activeTodos,
      type: SectionType.Active,
    })
  }

  if (completedTodos.length > 0) {
    sections.push({
      key: completedSectionKey,
      title: t('todo.filter.completed'),
      count: completedTodos.length,
      todos: completedTodos,
      type: completedSectionKey as any,
    })
  }

  return sections
}

/**
 * 현재 필터에 따라 섹션 데이터를 계산
 * @param currentFilter - 현재 필터 타입
 * @param sortedAndFilteredTodos - 정렬 및 필터링된 todo 리스트
 * @param locale - 로케일 정보
 * @param t - i18n translation 함수
 * @returns 계산된 섹션 배열
 */
export const calculateSections = (
  currentFilter: FilterType,
  sortedAndFilteredTodos: TodoItem[],
  locale: string,
  t: (key: string) => string,
): TodoSection[] => {
  switch (currentFilter) {
    case FilterType.Planned:
      return categorizeTodosBySection(sortedAndFilteredTodos, 1, SortType.Planned, locale)

    case FilterType.All:
      return createActiveAndCompletedSections(sortedAndFilteredTodos, SectionType.Completed, t)

    case FilterType.Today:
      return createActiveAndCompletedSections(sortedAndFilteredTodos, SectionType.TodayCompleted, t)

    case FilterType.FocusDate:
      return createActiveAndCompletedSections(
        sortedAndFilteredTodos,
        SectionType.ActiveCompleted,
        t,
      )

    default:
      // 기본 섹션 (Important, Overdue, Completed 등)
      return [
        {
          key: SectionType.Active,
          title: '',
          todos: sortedAndFilteredTodos,
          count: sortedAndFilteredTodos.length,
          type: SectionType.Active,
        },
      ]
  }
}

/**
 * 현재 필터가 섹션 접기/펼치기 기능을 지원하는지 확인
 * @param currentFilter - 현재 필터 타입
 * @returns 접기/펼치기 지원 여부
 */
export const supportsCollapsibleSections = (currentFilter: FilterType): boolean => {
  return (
    currentFilter === FilterType.Planned ||
    currentFilter === FilterType.Today ||
    currentFilter === FilterType.FocusDate ||
    currentFilter === FilterType.All
  )
}
